import React from "react";
import { CheckCircle, ArrowRight, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { formatINR } from "../../lib/payment_helpers";

/**
 * PaymentSuccess — shown after Cashfree confirms payment.
 *
 * Props:
 *   orderId       string
 *   rentalId      string
 *   amount        number   paise
 *   depositAmount number   paise
 */
export default function PaymentSuccess({ orderId, rentalId, amount, depositAmount }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      {/* Icon */}
      <div className="w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-6">
        <CheckCircle className="h-10 w-10 text-emerald-500" />
      </div>

      {/* Headline */}
      <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white mb-2">
        Deposit secured!
      </h1>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-6">
        {depositAmount
          ? `₹${(depositAmount / 100).toLocaleString("en-IN")} is now held safely in Orello's escrow.`
          : "Your deposit is now held safely in Orello's escrow."}{" "}
        It will be released only when both parties agree at move-out.
      </p>

      {/* Receipt card */}
      <div className="card-base w-full max-w-sm p-5 text-left mb-6 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Order ID</span>
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate max-w-[180px]">
            {orderId}
          </span>
        </div>
        {amount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Amount paid</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatINR(amount)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Status</span>
          <span className="text-emerald-600 font-semibold">Confirmed ✓</span>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
        {rentalId && (
          <Link
            to={`/app/rental/${rentalId}`}
            className="flex-1 flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition-colors"
          >
            View your deal <ArrowRight className="h-4 w-4" />
          </Link>
        )}
        <Link
          to="/app"
          className="flex-1 flex items-center justify-center gap-2 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 font-medium py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/04 transition-colors"
        >
          Go to dashboard
        </Link>
      </div>

      <p className="mt-6 text-xs text-gray-400">
        A payment receipt has been sent to your email.
      </p>
    </div>
  );
}
